/**
 * Browser-side error reporting for the beta. Uncaught errors and failed API
 * calls are kept in a short in-memory log (attached to bug reports), and
 * uncaught errors are also sent to the server, which groups them per day.
 */

export const CLIENT_VERSION = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev';

interface LoggedError {
  at: string;
  message: string;
}
interface LoggedRequest {
  at: string;
  method: string;
  url: string;
  status: number;
  code?: string;
}

const KEEP = 15;
const recentErrors: LoggedError[] = [];
const recentRequests: LoggedRequest[] = [];
const push = <T,>(list: T[], item: T) => {
  list.push(item);
  if (list.length > KEEP) list.shift();
};

/** Called by the API client for every request that didn't succeed. */
export function noteFailedRequest(method: string, url: string, status: number, code?: string) {
  push(recentRequests, { at: new Date().toISOString(), method, url: url.slice(0, 200), status, code });
  // Server errors are worth grouping too; 4xx are usually the player's own mistakes (wrong password…).
  if (status >= 500 || status === 0) void send({ kind: 'api', message: `${method} ${url.replace(/\d+/g, ':n')} → ${status || 'network error'}${code ? ` (${code})` : ''}` });
}

export function recentContext() {
  return { errors: [...recentErrors], requests: [...recentRequests] };
}

// Each distinct error is sent at most once a minute, and at most 25 a page load.
let sentThisLoad = 0;
const lastSent = new Map<string, number>();

async function send(e: { kind: 'error' | 'unhandledrejection' | 'react' | 'api'; message: string; source?: string; stack?: string }) {
  const key = `${e.kind}|${e.message}|${e.source ?? ''}`;
  const now = Date.now();
  if (sentThisLoad >= 25 || now - (lastSent.get(key) ?? 0) < 60_000) return;
  lastSent.set(key, now);
  sentThisLoad++;
  try {
    await fetch('/api/v1/client-errors', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        kind: e.kind,
        message: e.message.slice(0, 1000) || '(no message)',
        source: e.source?.slice(0, 300),
        stack: e.stack?.slice(0, 4000),
        clientVersion: CLIENT_VERSION,
        path: window.location.pathname.slice(0, 200),
      }),
      keepalive: true,
    });
  } catch {
    // Reporting must never cause more errors.
  }
}

export function reportError(kind: 'error' | 'unhandledrejection' | 'react', err: unknown, source?: string) {
  const error = err instanceof Error ? err : null;
  const message = error ? `${error.name}: ${error.message}` : String(err);
  push(recentErrors, { at: new Date().toISOString(), message: message.slice(0, 500) });
  void send({ kind, message, source, stack: error?.stack });
}

let installed = false;
export function installErrorReporting() {
  if (installed) return;
  installed = true;
  window.addEventListener('error', (ev) => {
    // Resource load failures (images, scripts) have no message; ignore them.
    if (!ev.message && !ev.error) return;
    // "Script error." means a cross-origin script hid the details; nothing useful to report.
    if (ev.message === 'Script error.' && !ev.error) return;
    reportError('error', ev.error ?? ev.message, ev.filename ? `${ev.filename}:${ev.lineno}:${ev.colno}` : undefined);
  });
  window.addEventListener('unhandledrejection', (ev) => {
    // Failed API calls are already in the request log (and server errors already reported).
    if ((ev.reason as Error | undefined)?.name === 'ApiError') return;
    reportError('unhandledrejection', ev.reason);
  });
}
